import { forwardRef, PropsWithChildren, useMemo, useState } from "react";
import * as Select from "@radix-ui/react-select";
import cx from "classnames";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import styles from "./multi-select-with-overflow.module.scss";

export type SelectOption = {
  label: string;
  value: string;
};

interface SelectItemProps {
  value: string;
  selected?: boolean;
  className?: string;
  onSelect: (value: string) => void;
}

const SelectItem = forwardRef(function SelectItem(
  {
    children,
    value,
    selected = false,
    className,
    onSelect,
  }: PropsWithChildren<SelectItemProps>,
  forwardedRef: React.Ref<HTMLDivElement>
) {
  return (
    <div
      ref={forwardedRef as React.RefObject<HTMLDivElement>}
      className={cx(styles.item, className)}
      onClick={() => onSelect(value)}
    >
      <Checkbox
        checked={selected}
        className={styles.checkbox}
        onCheckedChange={() => onSelect(value)}
      />
      <span className={styles.itemLabel}>{children}</span>
    </div>
  );
});

interface MultiSelectWithOverflowProps {
  options?: SelectOption[];
  value?: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  maxVisibleItems?: number;
}

export function MultiSelectWithOverflow({
  options = [],
  value = [],
  onChange,
  placeholder = "Select …",
  maxVisibleItems = 2,
}: MultiSelectWithOverflowProps) {
  const [open, setOpen] = useState(false);

  const selectedOptions = useMemo(() => {
    return options.filter((option) => value?.includes(option.value));
  }, [options, value]);

  const visibleOptions = useMemo(() => {
    return selectedOptions.slice(0, maxVisibleItems);
  }, [selectedOptions, maxVisibleItems]);

  const remainingCount = useMemo(() => {
    return Math.max(0, selectedOptions.length - maxVisibleItems);
  }, [selectedOptions.length, maxVisibleItems]);

  const handleSelect = (selectedValue: string) => {
    const newValues = value?.includes(selectedValue)
      ? value.filter((v) => v !== selectedValue)
      : [...(value || []), selectedValue];
    onChange(newValues);
  };

  return (
    <Select.Root open={open} onOpenChange={setOpen}>
      <Select.Trigger asChild>
        <div className={styles.select}>
          <div className={styles.trigger}>
            <div className={styles.triggerContent}>
              <div className={styles.badgesContainer}>
                {selectedOptions.length === 0 && (
                  <span className={styles.placeholder}>{placeholder}</span>
                )}
                {visibleOptions.map((option) => (
                  <Badge
                    key={option.value}
                    variant="secondary"
                    className={styles.badge}
                  >
                    <span className={styles.badgeLabel}>{option.label}</span>
                  </Badge>
                ))}
                {remainingCount > 0 && (
                  <Badge variant="secondary" className={styles.badge}>
                    +{remainingCount} more
                  </Badge>
                )}
              </div>
              <ChevronDownIcon className={styles.icon} />
            </div>
          </div>
        </div>
      </Select.Trigger>

      <Select.Portal>
        <Select.Content
          className={styles.content}
          position="popper"
          sideOffset={4}
          align="start"
          style={{ zIndex: 9999 }}
        >
          <Select.Viewport className={styles.viewport}>
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                selected={value?.includes(option.value)}
                onSelect={handleSelect}
              >
                {option.label}
              </SelectItem>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
